import { useRef, useState } from "react";
import { sounds } from "@/mocks/home";
import { playFlipSound } from "../utils/audio";

const soundMap: Record<number, Parameters<typeof playFlipSound>[0]> = {
  1: "crisp",
  2: "soft",
  3: "old",
  4: "pop",
  5: "tap",
  6: "rapid",
};

function SoundExperience() {
  const [activeId, setActiveId] = useState<number | null>(null);
  const [volume, setVolume] = useState(0.9);
  const playingRef = useRef<number | null>(null);

  const play = (id: number) => {
    const kind = soundMap[id];
    playFlipSound(kind, volume);
    setActiveId(id);
    if (playingRef.current) window.clearTimeout(playingRef.current);
    playingRef.current = window.setTimeout(() => setActiveId(null), 900);
  };

  return (
    <section id="sound" className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
              <i className="ri-sound-module-line"></i>
              翻页音效专区
            </span>
            <h2 className="mt-3 font-heading text-3xl text-foreground-950 md:text-4xl">
              点击试听 · 还原纸感翻页声
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              每条音效均由真实拾音录制并经过算法调校，点击卡片即可播放，配合翻页动画带来沉浸声画体验。
            </p>
          </div>

          <div className="flex items-center gap-3">
            <i className="ri-volume-down-line text-foreground-600"></i>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
              className="h-1.5 w-36 cursor-pointer appearance-none rounded-full bg-secondary-200 accent-primary-500"
              aria-label="音量调节"
            />
            <i className="ri-volume-up-line text-foreground-600"></i>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sounds.map((s) => {
            const active = activeId === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => play(s.id)}
                className={`group flex items-start gap-4 rounded-2xl border p-5 text-left transition-all ${
                  active
                    ? "border-primary-300 bg-primary-50"
                    : "border-background-200 bg-background-50 hover:border-primary-200 hover:bg-primary-50/60"
                }`}
              >
                <span
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors ${
                    active ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-800 group-hover:bg-primary-100 group-hover:text-primary-700"
                  }`}
                >
                  <i className={`text-xl ${active ? "ri-volume-up-line" : s.icon}`}></i>
                </span>
                <span className="flex-1">
                  <span className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="font-medium text-foreground-950">{s.name}</span>
                      <span className="text-xs text-foreground-500">{s.en}</span>
                    </span>
                    <span className="text-xs text-foreground-500">{s.duration}</span>
                  </span>
                  <span className="mt-1.5 block text-sm leading-relaxed text-foreground-600">{s.desc}</span>
                  <span className={`mt-3 inline-flex items-center gap-1 text-xs font-medium ${active ? "text-primary-600" : "text-secondary-600"}`}>
                    <i className={active ? "ri-volume-vibrate-line" : "ri-play-circle-line"}></i>
                    {active ? "正在播放" : "点击试听"}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default SoundExperience;